<template>
    <div class="form-group" :class="{ 'mb-4': !removeMargin }">
        <label for="contact_id">
            <strong>
                {{ title }}
                <span v-if="required" class="text-danger">*</span>
            </strong>
        </label>

        <v-select
            v-model="model"
            input-id="contact_id"
            name="contact_id"
            :options="contacts"
            :clearable="!isDisabled"
            placeholder="Select a Contact"
            @search="fetchOptions"
            :disabled="isDisabled"
            :get-option-label="getContactLabel"
        ></v-select>

        <div v-if="errors?.length" >
            <div class="alert alert-danger mt-2" role="alert" v-for="(error, index) in errors" :key="index">
                {{ error }}
            </div>
        </div>

    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import VSelect from 'vue-select';
import axios from 'axios';
import debounce from 'lodash/debounce';

// Define props
const props = defineProps({
    errors: {
        type: Array,
        default: () => [],
        required: false
    },
    contact: {
        type: Object,
        required: false
    },
    customer: {
        type: Object,
        required: false
    },
    disableWhenPassedContact: {
        type: Boolean,
        default: false,
        required: false
    },
    required: {
        type: Boolean,
        default: false,
        required: false
    },
    removeMargin: {
        type: Boolean,
        default: false,
        required: false
    },
    title: {
        type: String,
        default: 'Contact',
        required: false
    },
});

const model = defineModel({
    type: Object,
    default: null
});

// Define emits for backward compatibility
const emit = defineEmits(['update-contact']);

// Reactive data
const contacts = ref([]);
const isDisabled = ref(false);

// Methods
const fetchOptions = (search, loading) => {
    if (search.length > 2) {
        debounceFetchOptions(search, loading);
    }
};

const debounceFetchOptions = debounce(async (search, loading) => {
    try {
        loading(true);
        const url = route('api.movements.contacts.select', {
            search,
            customer: props.customer?.id ?? undefined
        });
        const { data } = await axios.get(url);
        contacts.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        console.error(error);
    } finally {
        loading(false);
    }
}, 500);

const fetchContacts = async () => {
    try {
        const params = new URLSearchParams();
        if (props.customer) {
            params.append('customer', props.customer.id);
        }
        const url = route('api.movements.contacts.select') + '?' + params.toString();
        const { data } = await axios.get(url);
        contacts.value = Array.isArray(data) ? data : Object.values(data);
    } catch (error) {
        console.error(error);
    }
};

const getContactLabel = (contact) => {
    if (!contact) {
        return '';
    }
    let label = contact.contact_name || '';
    if (contact.position) {
        label += ` (${contact.position})`;
    }
    return label;
};

onMounted(() => {
    fetchContacts();
});

onUnmounted(() => {
    debounceFetchOptions.cancel()
});

// Watchers
// If a new customer is passed in, reset state and fetch contacts
watch(() => props.customer, async (newCustomer) => {
    contacts.value = []
    model.value = null
    if (newCustomer) {
        await fetchContacts()
    }
});

watch(() => props.contact, (newContact) => {
    if (newContact) {
        model.value = contacts.value.find(contact => contact.id === newContact.id) ?? null;
    }
    if (props.disableWhenPassedContact) {
        isDisabled.value = true;
    }
});

// Because @open was deprecated, we need to watch the model for null and refresh the list
watch(model, async (newContact) => {
    if (newContact) {
        // Emit update-contact for backward compatibility with existing parent components
        emit('update-contact', newContact);
    } else {
        await fetchContacts();
    }
});

</script>
